<template>
  <div class="purchase-summary">
    <div class="summary">
      <div class="summary__subtotal">
        <div>
          <AmTypography
            class="text--dark-2"
            :text="$t('subtotal')"
            typography-style="l7"
          />
        </div>
        <div>
          <AmTypography
            class="text--dark-2"
            typography-style="l7"
          >
            <template>
              <div>
                {{ cart.subtotal | currency({ codeCurrency: cart.currency_code }) }}
              </div>
            </template>
          </AmTypography>
        </div>
      </div>
      <div
        v-if="enableAddress"
        class="summary__delivery"
      >
        <div>
          <AmTypography
            class="text--dark-2"
            text="Entrega"
            typography-style="l7"
          />
        </div>
        <div>
          <AmTypography
            typography-style="l7"
            text-color="success"
          >
            <template>
              <div
                v-if="cartFreight()"
                class="text--dark-2"
              >
                {{ cart.freight | currency({ codeCurrency: cart.currency_code }) }}
              </div>
              <div v-else>
                <span>{{ $t('containerRigth.requests.freeShipping') }}</span>
              </div>
            </template>
          </AmTypography>
        </div>
      </div>
      <div
        v-if="cart.coupon.id"
        class="summary__discount"
      >
        <div>
          <AmTypography
            class="text--dark-2"
            typography-style="l7"
          >
            <template>
              <div>
                {{ $t('discount') }}
              </div>
            </template>
          </AmTypography>
        </div>
        <div>
          <AmTypography
            class="text--dark-2"
            typography-style="l7"
          >
            <template>
              <div>-{{ cart.discount | currency({ codeCurrency: cart.currency_code }) }}</div>
            </template>
          </AmTypography>
        </div>
      </div>
      <hr />
      <div class="summary__total">
        <div>
          <AmTypography
            class="text--dark-2"
            text="Total"
            typography-style="l7"
            weight="bold"
          />
        </div>
        <div>
          <AmTypography
            class="text--dark-2"
            typography-style="l7"
            weight="bold"
          >
            <template>
              <div>
                {{ cart.total | currency({ codeCurrency: cart.currency_code }) }}
              </div>
            </template>
          </AmTypography>
        </div>
      </div>
      <div
        v-if="wasPaymentInCreditCard"
        class="summary__payment-type"
      >
        <div class="summary__payment-type__payment">
          <AmTypography
            :text="paymentTypeText"
            typography-style="l7"
            weight="bold"
          />
        </div>
        <div
          v-show="isPaymentForCard"
          class="summary__payment-type__installments"
        >
          <AmTypography typography-style="l7">
            <template>
              {{ installmentSeletedLabel }}
            </template>
          </AmTypography>
        </div>
      </div>
      <div
        v-if="showApplePayInfo"
        class="summary__payment-type"
      >
        <div class="summary__payment-type__payment">
          <AmTypography
            :text="`${paymentTypeText} ${getPaymentInfo.card.flag}`"
            typography-style="l7"
            weight="bold"
          />
        </div>
        <div class="summary__payment-type__installments">
          <AmTypography typography-style="l7">
            <template v-if="getPaymentInfo.card.type === 'credit'">
              {{ applePaySelectedInstallment }}
            </template>
            <template v-else>
              {{ cart.total | currency({ codeCurrency: cart.currency_code }) }}
            </template>
          </AmTypography>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import currencyFilterMixin from '@/components/mixins/currencyFilterMixin'
import {
  getApplePaymentInfo,
  clearApplePaymentInfo,
} from '@/utils/ApplePay/applePayPaymentInfoStorage'
import { mapGetters } from 'vuex'

export default {
  name: 'PurchaseSummary',
  mixins: [currencyFilterMixin],
  props: {
    paymentType: {
      type: String,
    },
    installmentSeletedLabel: {
      type: String,
    },
  },
  data() {
    return {
      cart: {},
      defaultCurrency: 'BRL',
    }
  },
  computed: {
    ...mapGetters('Card', ['cardType']),
    ...mapGetters('Checkout', ['getCart']),
    ...mapGetters('Cart', {
      enableAddress: 'getEnableAddress',
      getCurrencyCode: 'getCurrencyCode',
      getIsInternational: 'getIsInternational',
      getInstallments: 'getInstallments',
    }),
    currentPaymentTypeSelected() {
      return this.paymentType
    },
    paymentTypeText() {
      const paymentTexts = {
        card: this.$t('steps.payment.purchaseSummary.card'),
        billet: this.$t('steps.payment.purchaseSummary.billet'),
        pix: this.$t('steps.payment.purchaseSummary.pix'),
        applepay: this.$t(
          `steps.payment.purchaseSummary.applepay.${this.getPaymentInfo.card.type}`
        ),
      }

      return paymentTexts[this.currentPaymentTypeSelected]
    },
    isPaymentForCard() {
      return this.currentPaymentTypeSelected === 'card'
    },
    wasPaymentInCreditCard() {
      return (
        this.paymentType !== 'applepay' &&
        (this.cardType === '' || this.cardType === 'credito' || this.cardType === 'multiplo')
      )
    },
    applePaySelectedInstallment() {
      const selectedInstallment = this.getInstallments.find(
        ({ installment }) => installment.toString() === this.getPaymentInfo.installment
      )

      if (!selectedInstallment) {
        return this.$options.filters.currency(this.cart.total, {
          codeCurrency: this.cart.currency_code,
        })
      }

      return `${this.getPaymentInfo.installment}x de ${this.$options.filters.currency(
        selectedInstallment.value,
        { codeCurrency: this.cart.currency_code }
      )}`
    },
    showApplePayInfo() {
      return (
        this.paymentType === 'applepay' &&
        this.getPaymentInfo.card.flag &&
        this.getPaymentInfo.card.type
      )
    },
    getPaymentInfo() {
      return getApplePaymentInfo()
    },
  },
  watch: {
    getCart: {
      handler(cart) {
        this.cart = cart
      },
      deep: true,
      immediate: true,
    },
  },
  beforeDestroy() {
    clearApplePaymentInfo()
  },
  methods: {
    getTextColor() {
      return this.cartFreight() ? '' : 'success'
    },
    cartFreight() {
      return this.cart.freight > 0
    },
  },
}
</script>

<style lang="scss" scoped>
hr {
  margin: 5px 0;
}
.purchase-summary {
  .summary {
    &__subtotal,
    &__delivery,
    &__discount,
    &__total,
    &__payment-type {
      display: flex;
      justify-content: space-between;
      padding: {
        left: 10px;
      }
      margin-bottom: 4px;
    }

    @include mobile {
      &__subtotal,
      &__delivery,
      &__discount,
      &__total,
      &__payment-type {
        padding: {
          left: 0;
        }
      }

      &__payment-type {
        &__payment {
          width: 65%;
        }

        &__installments {
          width: 35%;
          text-align: right;
        }
      }
    }
  }
}
</style>
